import type { AnchorSnapshot, EdgeState, EngineLocation, FeedEngine, FollowRule, ScrollBehavior } from '../engine';
import { contentEquals } from './chatItem';
import {
  ChatFeedStore,
  type ChatFeedCursors,
  type ChatFeedStoreMutation,
  type ChatFeedStoreSnapshot
} from './store';
import type { ChatItem, ClientRequestId, FeedPage, MessageId, SortOrder, SyncCursor } from './types';

/** Page request direction used for guard state and request diagnostics. */
export type FeedRequestDirection = 'latest' | 'older' | 'newer' | 'around';
/** Reasons an in-flight page is discarded after a newer state wins. */
export type FeedGuardDropReason = 'stale-epoch' | 'stale-boundary' | 'stale-cursor';
/** Reasons a page request is not started. */
export type FeedGuardSkipReason = 'already-loading' | 'no-cursor';

/** Delay in ms before edge UI flips to loading for pagination requests. */
export const LOADING_EDGE_DELAY_MS = 150;

/** Maximum server-backed rows retained while traversing adjacent history pages. */
export const MAX_LOADED_SERVER_ROWS = 240;

/** Immutable controller snapshot combining ordered rows, edges, loading flags, and epoch. */
export interface FeedControllerSnapshot extends ChatFeedStoreSnapshot {
  readonly edgeState: EdgeState;
  readonly loading: {
    readonly older: boolean;
    readonly newer: boolean;
  };
  readonly windowEpoch: number;
}

/** Boundary cursor/id and epoch captured before a guarded page fetch starts. */
export interface FeedPageRequestContext {
  readonly boundaryId?: MessageId;
  readonly cursor?: SortOrder;
  readonly epoch: number;
}

/** Guarded fetch options; fetchPage rejects are converted into error results. */
export interface FeedGuardedPageFetchInput {
  readonly direction: FeedRequestDirection;
  readonly fetchPage: (context: FeedPageRequestContext) => Promise<FeedPage>;
  readonly limit: number;
  readonly loadingSide?: 'older' | 'newer';
  readonly pageDebug?: (page: FeedPage) => Record<string, unknown>;
  readonly reason?: string;
  readonly resetWindow?: boolean;
  readonly requestDebug?: Record<string, unknown>;
}

/** Guarded fetch outcome; callers handle errors through the result union instead of catch. */
export type FeedGuardedPageFetchResult =
  | { kind: 'accepted'; epoch: number; page: FeedPage }
  | { kind: 'dropped'; epoch: number; page?: FeedPage; reason: FeedGuardDropReason }
  | { kind: 'error'; epoch: number; error: unknown }
  | { kind: 'skipped'; reason: FeedGuardSkipReason };

/** Server resync diff already projected into render rows and durable ids. */
export interface FeedResyncDiff {
  readonly deletes: readonly MessageId[];
  readonly deleteSortOrders?: readonly SortOrder[];
  readonly updates: readonly ChatItem[];
  readonly creates: readonly ChatItem[];
}

/** Counts applied from a resync diff; historic creates may be withheld behind a newer cursor. */
export interface FeedResyncDiffResult {
  readonly removed: number;
  readonly updated: number;
  readonly appended: number;
  readonly withheldHistoricCreates: number;
}

/** Controller dependencies; engine is mutated synchronously and callbacks receive stripped debug data. */
export interface FeedControllerOptions {
  engine: FeedEngine<ChatItem>;
  loadingEdgeDelayMs?: number;
  onRequestEvent?: (kind: string, data: Record<string, unknown>) => void;
  store?: ChatFeedStore;
}

interface StartedPageRequest {
  boundary?: RequestBoundary;
  context: FeedPageRequestContext;
  debug: Record<string, unknown>;
  direction: FeedRequestDirection;
  loadingEdgeTimer?: ReturnType<typeof setTimeout>;
}

/** Coordinates feed store mutations with engine writes and guarded pagination epochs. */
export class FeedController {
  private readonly store: ChatFeedStore;
  private edgeState: EdgeState = { top: 'end', bottom: 'live' };
  private loading = { older: false, newer: false };
  private windowEpoch = 0;
  private subscribers = new Set<(snapshot: FeedControllerSnapshot) => void>();
  private cachedSnapshot: FeedControllerSnapshot | undefined;

  /** Uses the supplied store or creates an empty one; no DOM work happens until methods are called. */
  constructor(private readonly options: FeedControllerOptions) {
    this.store = options.store ?? new ChatFeedStore();
  }

  /**
   * Returns the current snapshot; the object is cached until the next publish so external
   * stores (React useSyncExternalStore) can compare by identity without looping.
   */
  snapshot(): FeedControllerSnapshot {
    this.cachedSnapshot ??= {
      ...this.store.snapshot(),
      edgeState: { ...this.edgeState },
      loading: { ...this.loading },
      windowEpoch: this.windowEpoch
    };
    return this.cachedSnapshot;
  }

  /** Subscribes immediately with the current snapshot and returns an idempotent unsubscribe. */
  subscribe(cb: (snapshot: FeedControllerSnapshot) => void): () => void {
    this.subscribers.add(cb);
    cb(this.snapshot());
    return () => this.subscribers.delete(cb);
  }

  /** Returns whether a captured request epoch can still commit into the current window. */
  isEpochCurrent(epoch: number): boolean {
    return this.windowEpoch === epoch;
  }

  /** Returns true when latest must be fetched before returning to present or appending sends. */
  isHistoricWindow(): boolean {
    return shouldFetchLatestForJump(this.edgeState.bottom, this.store.snapshot().newerCursor);
  }

  // Guarded commit for replace-type flows (§12): a fetch epoch invalidated by a
  // newer replacement must not install its window. Returns false when dropped.
  /** Replaces the whole window only when the captured epoch is current; returns false on stale epochs. */
  replaceWindowIfCurrent(
    epoch: number,
    items: ChatItem[],
    location: EngineLocation,
    cursors: ChatFeedCursors,
    edges?: EdgeState
  ): boolean {
    if (!this.isEpochCurrent(epoch)) return false;
    this.replaceWindow(items, location, cursors, edges);
    return true;
  }

  /** Installs authoritative latest rows while preserving unresolved local sends and active keys. */
  replaceLatestWindowIfCurrent(
    epoch: number,
    items: readonly ChatItem[],
    cursors: ChatFeedCursors
  ): boolean {
    if (!this.isEpochCurrent(epoch)) return false;
    this.replaceWindow(this.store.reconciledLatestItems(items, 'replace'), { kind: 'bottom' }, cursors);
    return true;
  }

  /** Merges an overlapping latest page while restoring the current visual anchor. */
  mergeLatestWindowIfCurrent(
    epoch: number,
    items: readonly ChatItem[],
    removedIds: readonly MessageId[],
    syncCursor: SyncCursor
  ): { readonly inserted: number } | undefined {
    if (!this.isEpochCurrent(epoch)) return undefined;
    const previous = this.store.snapshot();
    const anchor = this.options.engine.getAnchorSnapshot();
    // The engine reports an empty itemKey until its first anchor measurement, so
    // || (not ??) is required for the first-item fallback to engage.
    const anchorKey = anchor.itemKey || previous.items[0]?.itemKey;
    if (!anchorKey) return undefined;
    const nextItems = this.store.reconciledLatestItems(items, 'merge', removedIds);
    const previousIds = new Set(previous.items.flatMap((item) => item.id ? [item.id] : []));
    const inserted = nextItems.filter((item) => item.id && !previousIds.has(item.id)).length;
    this.replaceWindow(nextItems, {
      kind: 'item',
      key: anchorKey,
      align: 'start',
      offsetPx: anchor.offsetPx
    }, {
      olderCursor: previous.olderCursor,
      newerCursor: undefined,
      syncCursor
    });
    return { inserted };
  }

  /** Replaces rows, cursors, and edges, then writes the engine window at the supplied location. */
  replaceWindow(
    items: ChatItem[],
    location: EngineLocation,
    cursors: ChatFeedCursors,
    edges = edgesForCursors(cursors)
  ): void {
    const mutation = this.store.replaceWindow(items, cursors);
    this.windowEpoch += 1;
    this.edgeState = edges;
    this.invalidateSnapshotBeforeEngineMutation();
    this.options.engine.setWindow(mutation.next, location, edges);
    this.publish();
  }

  /** Applies edge patch only for the current epoch; returns false when stale. */
  setEdgesIfCurrent(epoch: number, patch: Partial<EdgeState>): boolean {
    if (!this.isEpochCurrent(epoch)) return false;
    this.setEdges(patch);
    return true;
  }

  /** Prepends an older ascending page, updates top edge, and returns the inserted row count. */
  applyOlderPage(items: ChatItem[], olderCursor: SortOrder | undefined): number {
    const anchor = this.options.engine.getAnchorSnapshot();
    const protectedKeys = this.currentProtectedKeys(anchor);
    const mutation = this.store.prependOlderBounded(items, olderCursor, MAX_LOADED_SERVER_ROWS, protectedKeys);
    this.invalidateSnapshotBeforeEngineMutation();
    if (mutation.pruned) {
      this.edgeState = edgesForCursors(this.store.snapshot());
      this.installReorderedWindow(mutation.previous, mutation.next, false, anchor);
      this.publish();
      return mutation.inserted.length;
    }
    if (!isHeadExtension(mutation.previous, mutation.next)) {
      this.edgeState = { ...this.edgeState, top: olderCursor ? 'idle' : 'end' };
      this.installReorderedWindow(mutation.previous, mutation.next, false);
      this.publish();
      return mutation.inserted.length;
    }

    this.options.engine.batch(() => {
      if (mutation.inserted.length > 0) {
        this.options.engine.prepend(mutation.inserted);
      }
      updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next);
      this.setEdges({ top: olderCursor ? 'idle' : 'end' }, false);
    });
    this.publish();
    return mutation.inserted.length;
  }

  /** Reopens a sealed top edge at the first loaded row without replacing the window. */
  reopenOlderFromLoadedStart(): SortOrder | undefined {
    const snapshot = this.store.snapshot();
    if (this.edgeState.top !== 'end' || snapshot.olderCursor) return undefined;

    const cursor = snapshot.items.find((item) => item.sortOrder !== undefined)?.sortOrder;
    if (!cursor || !this.store.reopenOlder(cursor)) return undefined;

    this.setEdges({ top: 'idle' });
    return cursor;
  }

  /** Appends a newer ascending page, updates bottom edge, and returns the inserted row count. */
  applyNewerPage(items: ChatItem[], newerCursor: SortOrder | undefined): number {
    const anchor = this.options.engine.getAnchorSnapshot();
    const protectedKeys = this.currentProtectedKeys(anchor);
    const mutation = this.store.appendNewerBounded(items, newerCursor, MAX_LOADED_SERVER_ROWS, protectedKeys);
    this.invalidateSnapshotBeforeEngineMutation();
    if (mutation.pruned) {
      this.edgeState = edgesForCursors(this.store.snapshot());
      this.installReorderedWindow(mutation.previous, mutation.next, false, anchor);
      this.publish();
      return mutation.inserted.length;
    }
    if (!isTailExtension(mutation.previous, mutation.next)) {
      this.edgeState = { ...this.edgeState, bottom: newerCursor ? 'idle' : 'live' };
      this.installReorderedWindow(mutation.previous, mutation.next, false);
      this.publish();
      return mutation.inserted.length;
    }

    this.options.engine.batch(() => {
      updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next);
      if (mutation.inserted.length > 0) {
        this.options.engine.append(mutation.inserted, false);
      }
      this.setEdges({ bottom: newerCursor ? 'idle' : 'live' }, false);
    });
    this.publish();
    return mutation.inserted.length;
  }

  /** Applies persisted resync deletes/updates/creates to loaded rows and returns applied counts. */
  applyResyncDiff(diff: FeedResyncDiff, follow: FollowRule): FeedResyncDiffResult {
    const previous = this.store.snapshot().items;
    const deleteSortOrders = diff.deleteSortOrders ?? [];
    const keysToRemove = itemKeysForIdentities(previous, diff.deletes, deleteSortOrders);

    this.store.removeLoadedItems(diff.deletes, deleteSortOrders);
    const afterDeletes = this.store.snapshot().items;
    const updated = loadedUpdateCount(afterDeletes, diff.updates);
    this.store.updateLoadedItems(diff.updates);

    let insertMutation: ChatFeedStoreMutation | undefined;
    let withheldHistoricCreates = 0;
    if (diff.creates.length > 0) {
      if (this.store.snapshot().newerCursor) {
        withheldHistoricCreates = diff.creates.length;
      } else {
        insertMutation = this.store.appendNewer([...diff.creates], undefined);
      }
    }
    const insertedCreates = insertMutation?.inserted ?? [];

    const next = this.store.snapshot().items;
    this.invalidateSnapshotBeforeEngineMutation();
    if (insertMutation && !isTailExtension(insertMutation.previous, insertMutation.next)) {
      // A create landed older than the loaded tail and was sorted into place;
      // installing the reordered window wholesale subsumes removes and updates.
      this.installReorderedWindow(previous, next, follow);
    } else {
      this.options.engine.batch(() => {
        for (const key of keysToRemove) {
          this.options.engine.remove(key);
        }
        updateChangedExistingItems(this.options.engine, previous, next);
        if (insertedCreates.length > 0) {
          this.options.engine.append(insertedCreates, follow);
        }
      }, follow);
    }
    this.publish();

    return {
      removed: keysToRemove.length,
      updated,
      appended: insertedCreates.length,
      withheldHistoricCreates
    };
  }

  /** Detaches a live tail by installing a newer cursor without inserting rows. */
  detachLiveTail(newerCursor: SortOrder): void {
    this.applyNewerPage([], newerCursor);
  }

  /** Appends live creates only for live-contiguous windows; historic windows report withholding. */
  applyLiveCreate(
    message: ChatItem,
    follow: FollowRule
  ): { kind: 'appended'; inserted: boolean } | { kind: 'historic' } {
    if (this.store.snapshot().newerCursor) return { kind: 'historic' };

    const mutation = this.store.appendLive(message);
    const appended = mutation.inserted.at(-1);
    this.invalidateSnapshotBeforeEngineMutation();
    if (!isTailExtension(mutation.previous, mutation.next)) {
      this.installReorderedWindow(mutation.previous, mutation.next, follow);
      this.publish();
      return { kind: 'appended', inserted: mutation.inserted.length > 0 };
    }

    this.options.engine.batch(() => {
      updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next);
      if (appended) this.options.engine.append([appended], follow);
    }, follow);
    this.publish();
    return { kind: 'appended', inserted: appended !== undefined };
  }

  /** Appends an optimistic local send and follows according to caller policy. */
  applyOptimisticSend(message: ChatItem, follow: FollowRule): { kind: 'appended' } {
    const mutation = this.store.appendOptimistic(message);
    const appended = mutation.inserted.at(-1);
    this.invalidateSnapshotBeforeEngineMutation();
    this.options.engine.batch(() => {
      updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next);
      if (appended) this.options.engine.append([appended], follow);
    }, follow);
    this.publish();
    return { kind: 'appended' };
  }

  /** Replaces a pending optimistic row by clientRequestId; returns false when no pending row matches. */
  reconcileSendAck(clientRequestId: ClientRequestId, message: ChatItem): boolean {
    const mutation = this.store.reconcileSendAck(clientRequestId, message);
    if (!mutation) return false;

    this.invalidateSnapshotBeforeEngineMutation();
    this.options.engine.batch(() => updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next));
    this.publish();
    return true;
  }

  /** Marks a pending optimistic row failed; returns false when no sending row matches. */
  markSendFailed(clientRequestId: ClientRequestId): boolean {
    const mutation = this.store.markSendFailed(clientRequestId);
    if (!mutation) return false;

    this.invalidateSnapshotBeforeEngineMutation();
    this.options.engine.batch(() => updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next));
    this.publish();
    return true;
  }

  /** Updates one loaded server-backed row; returns false when the row is absent. */
  applyLoadedUpdate(message: ChatItem): boolean {
    const mutation = this.store.updateLoadedItem(message);
    if (!mutation) return false;

    this.invalidateSnapshotBeforeEngineMutation();
    this.options.engine.batch(() => updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next));
    this.publish();
    return true;
  }

  /** Re-projects loaded rows after entity changes; returns false when no row content changed. */
  applyEntityFanout(items: readonly ChatItem[]): boolean {
    const mutation = this.store.updateLoadedItems(items);
    if (!mutation) return false;

    this.invalidateSnapshotBeforeEngineMutation();
    this.options.engine.batch(() => updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next));
    this.publish();
    return true;
  }

  /** Removes loaded rows by durable id first and sort order fallback. */
  removeLoadedItems(ids: readonly MessageId[], sortOrders: readonly SortOrder[] = []): boolean {
    const keysToRemove = itemKeysForIdentities(this.store.snapshot().items, ids, sortOrders);
    const mutation = this.store.removeLoadedItems(ids, sortOrders);
    if (!mutation) return false;

    this.invalidateSnapshotBeforeEngineMutation();
    this.options.engine.batch(() => {
      for (const key of keysToRemove) {
        this.options.engine.remove(key);
      }
      updateChangedExistingItems(this.options.engine, mutation.previous, mutation.next);
    });
    this.publish();
    return true;
  }

  /** Starts a page fetch with epoch/boundary guards and returns accepted/dropped/error/skipped. */
  async guardedPageFetch(input: FeedGuardedPageFetchInput): Promise<FeedGuardedPageFetchResult> {
    const start = this.startPageRequest(input);
    if (start.kind === 'skipped') return start;

    try {
      const page = await input.fetchPage(start.context);
      const dropReason = this.dropReason(start);
      if (dropReason) {
        this.emitRequestEvent('request:drop', {
          ...start.debug,
          page: input.pageDebug?.(page),
          reason: dropReason
        });
        this.restoreDroppedLoadingEdge(input.loadingSide);
        return { kind: 'dropped', epoch: start.context.epoch, page, reason: dropReason };
      }

      this.emitRequestEvent('request:accept', {
        ...start.debug,
        page: input.pageDebug?.(page)
      });
      return { kind: 'accepted', epoch: start.context.epoch, page };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'unhandled failure';
      const dropReason = this.dropReason(start);
      if (dropReason) {
        this.emitRequestEvent('request:drop', {
          ...start.debug,
          error: message,
          reason: dropReason
        });
        this.restoreDroppedLoadingEdge(input.loadingSide);
        return { kind: 'dropped', epoch: start.context.epoch, reason: dropReason };
      }

      this.emitRequestEvent('request:error', {
        ...start.debug,
        error: message
      });
      if (input.loadingSide === 'older') this.setEdges({ top: 'error' }, false);
      if (input.loadingSide === 'newer') this.setEdges({ bottom: 'error' }, false);
      return { kind: 'error', epoch: start.context.epoch, error };
    } finally {
      this.clearLoadingEdgeTimer(start.loadingEdgeTimer);
      this.setLoading(input.loadingSide, false);
    }
  }

  /** Delegates an imperative engine scroll to an item anchor or bottom location. */
  scrollTo(location: EngineLocation, behavior?: ScrollBehavior): void {
    this.options.engine.scrollTo(location, behavior);
  }

  private startPageRequest(
    input: FeedGuardedPageFetchInput
  ):
    | ({ kind: 'started' } & StartedPageRequest)
    | { kind: 'skipped'; reason: FeedGuardSkipReason } {
    const snapshot = this.store.snapshot();
    if (input.loadingSide && this.loading[input.loadingSide]) {
      this.emitRequestEvent('request:skip', {
        ...input.requestDebug,
        direction: input.direction,
        reason: 'already-loading'
      });
      return { kind: 'skipped', reason: 'already-loading' };
    }

    const boundary = this.captureBoundary(input.direction, snapshot);
    if (isMissingBoundary(boundary)) {
      this.emitRequestEvent('request:skip', {
        ...input.requestDebug,
        direction: input.direction,
        reason: 'no-cursor',
        ...boundary.debug
      });
      return { kind: 'skipped', reason: 'no-cursor' };
    }

    if (input.resetWindow) {
      this.windowEpoch += 1;
    }

    const context = {
      boundaryId: boundary?.id,
      cursor: boundary?.cursor,
      epoch: this.windowEpoch
    };
    const debug = {
      ...input.requestDebug,
      direction: input.direction,
      epoch: context.epoch,
      limit: input.limit,
      reason: input.reason,
      ...boundary?.debug
    };
    const started = {
      boundary,
      context,
      debug,
      direction: input.direction
    };
    const loadingEdgeTimer = this.scheduleLoadingEdge(input.loadingSide, started);
    this.setLoading(input.loadingSide, true);
    this.emitRequestEvent('request:start', debug);

    return {
      kind: 'started',
      loadingEdgeTimer,
      boundary,
      context,
      debug,
      direction: input.direction
    };
  }

  private captureBoundary(
    direction: FeedRequestDirection,
    snapshot: ChatFeedStoreSnapshot
  ): RequestBoundary | MissingBoundary | undefined {
    if (direction === 'older') {
      const firstId = this.store.firstLoadedId();
      if (!firstId || !snapshot.olderCursor) {
        return { missing: true, debug: { firstId, olderCursor: snapshot.olderCursor } };
      }
      return {
        side: 'older',
        id: firstId,
        cursor: snapshot.olderCursor,
        debug: { firstId, olderCursor: snapshot.olderCursor }
      };
    }

    if (direction === 'newer') {
      const lastId = this.store.lastLoadedId();
      if (!lastId || !snapshot.newerCursor) {
        return { missing: true, debug: { lastId, newerCursor: snapshot.newerCursor } };
      }
      return {
        side: 'newer',
        id: lastId,
        cursor: snapshot.newerCursor,
        debug: { lastId, newerCursor: snapshot.newerCursor }
      };
    }

    return undefined;
  }

  private dropReason(start: {
    context: FeedPageRequestContext;
    boundary?: RequestBoundary;
  }): FeedGuardDropReason | undefined {
    if (start.context.epoch !== this.windowEpoch) return 'stale-epoch';
    if (!start.boundary) return undefined;

    const snapshot = this.store.snapshot();
    if (start.boundary.side === 'older') {
      if (this.store.firstLoadedId() !== start.boundary.id) return 'stale-boundary';
      if (snapshot.olderCursor !== start.boundary.cursor) return 'stale-cursor';
    } else {
      if (this.store.lastLoadedId() !== start.boundary.id) return 'stale-boundary';
      if (snapshot.newerCursor !== start.boundary.cursor) return 'stale-cursor';
    }

    return undefined;
  }

  private restoreDroppedLoadingEdge(side: 'older' | 'newer' | undefined): void {
    if (!side) return;

    // Restore only the dropped side so an in-flight opposite-direction request
    // keeps its loading edge. On stale-epoch drops the snapshot may still be the
    // pre-replacement window; the replacement's setWindow overwrites this edge,
    // and if that replacement fails the edge is no longer stuck on 'loading'.
    const edges = edgesForCursors(this.store.snapshot());
    if (side === 'older') {
      this.setEdges({ top: edges.top }, false);
    } else {
      this.setEdges({ bottom: edges.bottom }, false);
    }
  }

  private scheduleLoadingEdge(
    side: 'older' | 'newer' | undefined,
    start: StartedPageRequest
  ): ReturnType<typeof setTimeout> | undefined {
    if (!side) return undefined;

    // The delayed write publishes: it fires from a timer task, after the
    // request-start publish, so subscribers only learn about it from here.
    const writeLoadingEdge = () => {
      if (this.dropReason(start)) return;
      if (side === 'older') {
        this.setEdges({ top: 'loading' });
      } else {
        this.setEdges({ bottom: 'loading' });
      }
    };
    const delayMs = Math.max(0, this.options.loadingEdgeDelayMs ?? LOADING_EDGE_DELAY_MS);
    if (delayMs === 0) {
      writeLoadingEdge();
      return undefined;
    }

    return setTimeout(writeLoadingEdge, delayMs);
  }

  private clearLoadingEdgeTimer(timer: ReturnType<typeof setTimeout> | undefined): void {
    if (timer !== undefined) clearTimeout(timer);
  }

  // Sorted insertion can land rows in the middle of the window; the engine only
  // extends at the edges, so a non-edge insert re-installs the full ordered window
  // at a viewport-preserving (or follow-driven) location.
  private installReorderedWindow(
    previous: ChatItem[],
    next: ChatItem[],
    follow: FollowRule,
    capturedAnchor?: AnchorSnapshot
  ): void {
    this.options.engine.setWindow(next, this.reorderLocation(previous, follow, capturedAnchor), this.edgeState);
  }

  private reorderLocation(
    previous: ChatItem[],
    follow: FollowRule,
    capturedAnchor?: AnchorSnapshot
  ): EngineLocation {
    if (follow === 'force' || follow === 'force-smooth') return { kind: 'bottom' };

    const scrollState = this.options.engine.getScrollState();
    if ((follow === 'pin' || follow === 'pin-smooth') && scrollState.atBottom && this.edgeState.bottom === 'live') {
      return { kind: 'bottom' };
    }

    const anchor = capturedAnchor ?? this.options.engine.getAnchorSnapshot();
    // The engine reports an empty itemKey until its first anchor measurement, so
    // || (not ??) is required for the first-item fallback to engage.
    const anchorKey = anchor.itemKey || previous[0]?.itemKey;
    if (!anchorKey) return { kind: 'bottom' };
    return { kind: 'item', key: anchorKey, align: 'start', offsetPx: anchor.offsetPx };
  }

  private currentProtectedKeys(anchor: AnchorSnapshot): ReadonlySet<string> {
    const keys = new Set(this.options.engine.getWindow().items.map((item) => item.itemKey));
    // Preserve reorderLocation's fallback until the engine reports its first measured anchor.
    const anchorKey = anchor.itemKey || this.store.snapshot().items[0]?.itemKey;
    if (anchorKey) keys.add(anchorKey);
    const focusPinnedKey = this.options.engine.getFocusPinnedKey();
    if (focusPinnedKey) keys.add(focusPinnedKey);
    return keys;
  }

  private setEdges(patch: Partial<EdgeState>, publish = true): void {
    this.edgeState = { ...this.edgeState, ...patch };
    this.invalidateSnapshotBeforeEngineMutation();
    this.options.engine.setEdges(patch);
    if (publish) this.publish();
  }

  private setLoading(side: 'older' | 'newer' | undefined, value: boolean): void {
    if (!side) {
      this.publish();
      return;
    }

    this.loading = { ...this.loading, [side]: value };
    this.publish();
  }

  private publish(): void {
    this.cachedSnapshot = undefined;
    const snapshot = this.snapshot();
    this.subscribers.forEach((subscriber) => subscriber(snapshot));
  }

  private invalidateSnapshotBeforeEngineMutation(): void {
    // Engine flushes notify scroll/window subscribers synchronously before publish();
    // those callbacks must observe the post-store/controller state.
    this.cachedSnapshot = undefined;
  }

  private emitRequestEvent(kind: string, data: Record<string, unknown>): void {
    this.options.onRequestEvent?.(kind, stripUndefined(data));
  }
}

interface RequestBoundary {
  cursor: SortOrder;
  debug: Record<string, unknown>;
  id: MessageId;
  side: 'older' | 'newer';
}

interface MissingBoundary {
  debug: Record<string, unknown>;
  missing: true;
}

function isMissingBoundary(boundary: RequestBoundary | MissingBoundary | undefined): boundary is MissingBoundary {
  return Boolean(boundary && 'missing' in boundary);
}

// §11.4: a window is historic — return-to-present and composer send must fetch
// latest instead of scrolling/appending — unless the bottom edge is live-contiguous
// with no newer cursor.
/** Returns whether bottom navigation must fetch latest instead of trusting current contiguity. */
export function shouldFetchLatestForJump(
  edgeBottom: EdgeState['bottom'],
  newerCursor: SortOrder | undefined
): boolean {
  return edgeBottom !== 'live' || Boolean(newerCursor);
}

function isTailExtension(previous: ChatItem[], next: ChatItem[]): boolean {
  if (next.length < previous.length) return false;
  return previous.every((item, index) => next[index]?.itemKey === item.itemKey);
}

function isHeadExtension(previous: ChatItem[], next: ChatItem[]): boolean {
  if (next.length < previous.length) return false;
  const offset = next.length - previous.length;
  return previous.every((item, index) => next[offset + index]?.itemKey === item.itemKey);
}

function edgesForCursors(cursors: ChatFeedCursors): EdgeState {
  return {
    top: cursors.olderCursor ? 'idle' : 'end',
    bottom: cursors.newerCursor ? 'idle' : 'live'
  };
}

function updateChangedExistingItems(
  engine: FeedEngine<ChatItem>,
  current: ChatItem[],
  next: ChatItem[]
): void {
  const currentByKey = new Map(current.map((item) => [item.itemKey, item]));
  for (const nextItem of next) {
    const currentItem = currentByKey.get(nextItem.itemKey);
    if (currentItem && !contentEquals(currentItem, nextItem)) {
      engine.update(nextItem.itemKey, nextItem);
    }
  }
}

function itemKeysForIdentities(
  items: readonly ChatItem[],
  ids: readonly MessageId[],
  sortOrders: readonly SortOrder[]
): string[] {
  const idsToRemove = new Set(ids);
  const sortOrdersToRemove = new Set(sortOrders);
  return items.flatMap((item) => (
    (item.id && idsToRemove.has(item.id)) || (item.sortOrder && sortOrdersToRemove.has(item.sortOrder))
      ? [item.itemKey]
      : []
  ));
}

function loadedUpdateCount(items: readonly ChatItem[], updates: readonly ChatItem[]): number {
  const itemIndexesByKey = new Map(items.map((item, index) => [item.itemKey, index]));
  const itemIndexesById = new Map<MessageId, number>();
  items.forEach((item, index) => {
    if (item.id) itemIndexesById.set(item.id, index);
  });

  const indexes = new Set<number>();
  for (const update of updates) {
    const idIndex = update.id ? itemIndexesById.get(update.id) : undefined;
    const index = idIndex ?? itemIndexesByKey.get(update.itemKey);
    if (index !== undefined) indexes.add(index);
  }
  return indexes.size;
}

function stripUndefined(input: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined));
}
